import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { Eye, CursorClick, TrendUp, CalendarBlank } from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";

interface DailyCount {
  date: string;
  count: number;
}

interface PageCount {
  page: string;
  count: number;
}

type Period = '7d' | '30d' | '90d';

export function AdminStats() {
  const [period, setPeriod] = useState<Period>('30d');
  const [dailyViews, setDailyViews] = useState<DailyCount[]>([]);
  const [pageViews, setPageViews] = useState<PageCount[]>([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalContacts, setTotalContacts] = useState(0);
  const [contactsByType, setContactsByType] = useState<{ type: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  const getDaysAgo = (p: Period) => {
    const days = p === '7d' ? 7 : p === '30d' ? 30 : 90;
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString();
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const since = getDaysAgo(period);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const [viewsRes, contactsRes] = await Promise.all([
        supabase
          .from('page_views')
          .select('page_path, created_at')
          .eq('site_user_id', user.id)
          .gte('created_at', since)
          .order('created_at', { ascending: true }),
        supabase
          .from('contact_clicks')
          .select('contact_type, created_at')
          .eq('site_user_id', user.id)
          .gte('created_at', since),
      ]);

      const views = viewsRes.data || [];
      const contacts = contactsRes.data || [];

      setTotalViews(views.length);
      setTotalContacts(contacts.length);

      // Daily views
      const dailyMap = new Map<string, number>();
      views.forEach((v: any) => {
        const day = v.created_at.substring(0, 10);
        dailyMap.set(day, (dailyMap.get(day) || 0) + 1);
      });

      // Fill in missing days
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const dailyData: DailyCount[] = [];
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = d.toISOString().substring(0, 10);
        dailyData.push({ date: key, count: dailyMap.get(key) || 0 });
      }
      setDailyViews(dailyData);

      // Page views breakdown
      const pageMap = new Map<string, number>();
      views.forEach((v: any) => {
        const path = v.page_path || '/';
        pageMap.set(path, (pageMap.get(path) || 0) + 1);
      });
      const pageData = Array.from(pageMap.entries())
        .map(([page, count]) => ({ page, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      setPageViews(pageData);

      // Contacts by type
      const typeMap = new Map<string, number>();
      contacts.forEach((c: any) => {
        const t = c.contact_type || 'email';
        typeMap.set(t, (typeMap.get(t) || 0) + 1);
      });
      setContactsByType(
        Array.from(typeMap.entries()).map(([type, count]) => ({ type, count }))
      );

      setLoading(false);
    };

    load();
  }, [period]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'rgba(247,246,242,0.04)',
    border: '0.5px solid rgba(247,246,242,0.08)',
    padding: 24,
    fontFamily: monoFont,
  };

  const statCardStyle: React.CSSProperties = {
    ...cardStyle,
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  };

  if (loading) {
    return (
      <div style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.5)', padding: 40 }}>
        Carregando estatísticas...
      </div>
    );
  }

  return (
    <div style={{ fontFamily: monoFont, maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <h2 style={{ fontSize: 18, fontWeight: 300, color: 'rgba(247,246,242,0.85)' }}>
          Estatísticas
        </h2>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['7d', '30d', '90d'] as Period[]).map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                padding: '6px 12px',
                border: '0.5px solid rgba(247,246,242,0.12)',
                backgroundColor: period === p ? 'rgba(247,246,242,0.1)' : 'transparent',
                color: period === p ? '#F7F6F2' : 'rgba(247,246,242,0.4)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {p === '7d' ? '7 dias' : p === '30d' ? '30 dias' : '90 dias'}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div style={statCardStyle}>
          <Eye size={20} weight="thin" style={{ color: 'rgba(247,246,242,0.3)' }} />
          <div>
            <div style={{ fontSize: 28, fontWeight: 300, color: '#F7F6F2' }}>{totalViews}</div>
            <div style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)', marginTop: 2 }}>visualizações</div>
          </div>
        </div>
        <div style={statCardStyle}>
          <CursorClick size={20} weight="thin" style={{ color: 'rgba(247,246,242,0.3)' }} />
          <div>
            <div style={{ fontSize: 28, fontWeight: 300, color: '#F7F6F2' }}>{totalContacts}</div>
            <div style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)', marginTop: 2 }}>cliques em contato</div>
          </div>
        </div>
        <div style={statCardStyle}>
          <TrendUp size={20} weight="thin" style={{ color: 'rgba(247,246,242,0.3)' }} />
          <div>
            <div style={{ fontSize: 28, fontWeight: 300, color: '#F7F6F2' }}>
              {dailyViews.length > 0 ? Math.round(totalViews / dailyViews.length) : 0}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)', marginTop: 2 }}>média/dia</div>
          </div>
        </div>
      </div>

      {/* Daily views chart */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: 'rgba(247,246,242,0.5)', marginBottom: 16 }}>
          Visualizações por dia
        </div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyViews}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(247,246,242,0.06)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="rgba(247,246,242,0.2)"
                tick={{ fontSize: 12, fill: 'rgba(247,246,242,0.4)', fontFamily: monoFont }}
                interval={period === '7d' ? 0 : period === '30d' ? 4 : 13}
              />
              <YAxis
                stroke="rgba(247,246,242,0.2)"
                tick={{ fontSize: 12, fill: 'rgba(247,246,242,0.4)', fontFamily: monoFont }}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(247,246,242,0.06)',
                  border: '0.5px solid rgba(247,246,242,0.12)',
                  fontFamily: monoFont,
                  fontSize: 12,
                  color: '#F7F6F2',
                }}
                labelFormatter={formatDate}
                formatter={(value: number) => [value, 'views']}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="rgba(247,246,242,0.6)"
                strokeWidth={1.5}
                dot={false}
                activeDot={{ r: 3, fill: '#F7F6F2' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Page views breakdown */}
      {pageViews.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: 'rgba(247,246,242,0.5)', marginBottom: 16 }}>
            Páginas mais visitadas
          </div>
          <div style={{ width: '100%', height: Math.max(160, pageViews.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={pageViews} layout="vertical" margin={{ left: 80 }}>
                <XAxis
                  type="number"
                  stroke="rgba(247,246,242,0.2)"
                  tick={{ fontSize: 12, fill: 'rgba(247,246,242,0.4)', fontFamily: monoFont }}
                  allowDecimals={false}
                />
                <YAxis
                  type="category"
                  dataKey="page"
                  stroke="rgba(247,246,242,0.2)"
                  tick={{ fontSize: 12, fill: 'rgba(247,246,242,0.5)', fontFamily: monoFont }}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(247,246,242,0.06)',
                    border: '0.5px solid rgba(247,246,242,0.12)',
                    fontFamily: monoFont,
                    fontSize: 12,
                    color: '#F7F6F2',
                  }}
                  formatter={(value: number) => [value, 'views']}
                />
                <Bar dataKey="count" fill="rgba(247,246,242,0.25)" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Contact clicks breakdown */}
      {contactsByType.length > 0 && (
        <div style={cardStyle}>
          <div style={{ fontSize: 13, color: 'rgba(247,246,242,0.5)', marginBottom: 16 }}>
            Cliques em contato por tipo
          </div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {contactsByType.map(c => (
              <div key={c.type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20, fontWeight: 300, color: '#F7F6F2' }}>{c.count}</span>
                <span style={{ fontSize: 12, color: 'rgba(247,246,242,0.4)' }}>{c.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
